// "Complete this debt" message under a debt that is missing an amount owed or a repayment (PB-326).
// Display only. It can be used on any list of debts, not just the Debts page.

import { Box, Button, Typography } from '@/ui';
import type { DebtFormEntry } from '@/shared/api/types';
import { type DebtPromptField, getDebtMissingPrompt } from '@/shared/debts/getDebtMissingPrompt';

interface DebtMissingPromptProps {
  entry: Pick<DebtFormEntry, 'amountOwed' | 'repaymentAmount'>;
  /** The link was tapped: open the debt's edit view at this field. */
  onAction: (field: DebtPromptField) => void;
  disabled?: boolean;
}

function DebtMissingPrompt({ entry, onAction, disabled = false }: DebtMissingPromptProps): JSX.Element | null {
  const prompt = getDebtMissingPrompt(entry);
  if (!prompt) return null;

  return (
    <Box
      data-testid="components-debt-missing-prompt-container"
      sx={{
        mt: 1.5,
        px: 1.75,
        py: 1.25,
        backgroundColor: '#f1f5f5',
        borderLeft: '3px solid #0b5d4f',
      }}
    >
      <Typography
        data-testid="components-debt-missing-prompt-text"
        variant="body2"
        sx={{ fontSize: '14px', lineHeight: '20px', color: '#003d40' }}
      >
        {prompt.message}
      </Typography>
      <Button
        data-testid="components-debt-missing-prompt-btn-link"
        variant="text"
        disableRipple
        onClick={() => onAction(prompt.focusField)}
        disabled={disabled}
        sx={{
          p: 0,
          minWidth: 0,
          mt: 0.5,
          fontSize: '14px',
          fontWeight: 700,
          textTransform: 'none',
          textDecoration: 'underline',
          color: '#0b5d4f',
          '&:hover': {
            backgroundColor: 'transparent',
            textDecoration: 'underline',
          },
        }}
      >
        {prompt.linkLabel}
      </Button>
    </Box>
  );
}

export default DebtMissingPrompt;
